// The rules that decide what a drag or swipe on a bottom sheet does. PopoverHostComponent
// wires these to touch and mouse events; this file has no DOM and no Angular, so the rules
// can be unit tested. Before df-913afaedfad3 the handle's rule was written out once for touch
// and again for mouse, so a change to one silently missed the other.

// a downward swipe dismisses when it travels at least this far...
export const SWIPE_MIN_DISTANCE_PX = 50;
// ...and moves faster than this, in pixels per millisecond
export const SWIPE_MIN_VELOCITY_PX_PER_MS = 0.3;
// a press that moves no further than this counts as a tap on the handle
export const TAP_MAX_MOVEMENT_PX = 10;
// a handle drag past this fraction of the sheet's own height dismisses, however slow
export const DISMISS_FRACTION_OF_SHEET_HEIGHT = 0.5;

export enum HandleRelease {
  Tap = 'tap',           // dismiss straight away and let the CSS dismiss transition run
  Dismiss = 'dismiss',   // slide the sheet off screen, then dismiss
  SnapBack = 'snapBack', // not far or fast enough: return the sheet to where it was
}

// one press-drag-release on the bottom sheet's handle, for one kind of pointer (touch and
// mouse each get their own tracker, so one cannot end the other's drag)
export class HandleDragTracker {
  private startY = 0;
  private startTimeMs = 0;
  private dragging = false;
  private movedBeyondTap = false;

  start(y: number, timeMs: number): void {
    this.startY = y;
    this.startTimeMs = timeMs;
    this.dragging = true;
    this.movedBeyondTap = false;
  }

  // the downward offset to draw the sheet at, or undefined when no drag is in progress;
  // the sheet follows the pointer down 1:1 and never moves up past where it started
  move(y: number): number | undefined {
    if (!this.dragging) {
      return undefined;
    }
    const deltaY = y - this.startY;
    if (Math.abs(deltaY) > TAP_MAX_MOVEMENT_PX) {
      this.movedBeyondTap = true;
    }
    return deltaY > 0 ? deltaY : 0;
  }

  // what the release does, or undefined when no drag was in progress. The sheet height is read
  // only when the release is not a tap, because reading it forces a layout
  end(y: number, timeMs: number, sheetHeightPx: () => number): HandleRelease | undefined {
    if (!this.dragging) {
      return undefined;
    }
    this.dragging = false;

    const deltaY = y - this.startY;
    const velocity = deltaY / (timeMs - this.startTimeMs);

    if (!this.movedBeyondTap && Math.abs(deltaY) <= TAP_MAX_MOVEMENT_PX) {
      return HandleRelease.Tap;
    }
    const fastSwipe = deltaY > SWIPE_MIN_DISTANCE_PX && velocity > SWIPE_MIN_VELOCITY_PX_PER_MS;
    const pastDismissPoint = deltaY > sheetHeightPx() * DISMISS_FRACTION_OF_SHEET_HEIGHT;
    return fastSwipe || pastDismissPoint ? HandleRelease.Dismiss : HandleRelease.SnapBack;
  }
}

// a swipe on the sheet's body (not its handle) dismisses only as a fast downward swipe: a tap
// does nothing, there is no half-height rule because the body does not follow the finger, and
// the velocity is taken without its sign
export function isBodySwipeDismiss(deltaY: number, deltaTimeMs: number): boolean {
  const velocity = Math.abs(deltaY / deltaTimeMs);
  return deltaY > SWIPE_MIN_DISTANCE_PX && velocity > SWIPE_MIN_VELOCITY_PX_PER_MS;
}
